'use client';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { ReactNode, createContext, useEffect, useMemo } from 'react';
import { FormProvider, UseFormReturn, useForm, useWatch } from 'react-hook-form';
import type { StorefrontItemBody } from '@/api/menu-list/storefront-menu-snapshot.types';
import type { ResolvedStorefrontItem } from '@/entities/product/lib/storefront-menu';
import { useStorefrontItemsIndex } from '@/entities/product/lib/useStorefrontMenu';
import { useGetPageUrl } from '@/schema/useGetPageUrl';
import { applyParentFallbacks } from '@/shared/lib/apply-parent-fallbacks';
import { compareAssets } from '@/shared/lib/getMainAsset';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { computeAddonDefaults, type AddonSelections, type ItemsMap } from './compute-addon-defaults';
import { useVariantSwitcher } from './use-variant-switcher';

export type FormValue = {
    quantity: number;
    variant?: string;
    addons?: AddonSelections;
    memberVariants?: Record<string, string>;
    note?: string;
};

type ContextValue = {
    data?: StorefrontItemBody;
    currentItem?: StorefrontItemBody;
    slug: string;
    hash: string;
    formMethods: UseFormReturn<FormValue>;
    id?: string;
    switchVariant: (newVariant: string) => void;
};

export const ProductDetailContext = createContext<ContextValue>({} as ContextValue);

export type ProductDetailProviderProps = {
    children?: ReactNode;
    hash: string;
    id?: string;
    initialQuantity?: number;
    initialVariant?: string;
    initialAddons?: AddonSelections;
    initialMemberVariants?: Record<string, string>;
    initialNote?: string;
    isChangingByURL?: boolean;
};

const sortAssets = (item: StorefrontItemBody): StorefrontItemBody => ({
    ...item,
    assets: [...(item.assets ?? [])].sort(compareAssets),
});

export const ProductDetailProvider = ({
    children,
    hash,
    id,
    initialQuantity = 1,
    initialVariant,
    initialAddons,
    initialMemberVariants,
    initialNote,
    isChangingByURL = false,
}: ProductDetailProviderProps) => {
    const router = useRouter();
    const getPageUrl = useGetPageUrl();
    const pathname = usePathname();
    const pathnameParts = (pathname ?? '').split('/');
    const itemSlugParts = pathnameParts[pathnameParts.length - 1].split('-');
    const urlHash = itemSlugParts[itemSlugParts.length - 1];
    const parentHash = hash ?? urlHash;

    const itemsMap = useStorefrontItemsIndex();

    const item = itemsMap[parentHash];

    const data = useMemo(() => {
        if (!item) return undefined;
        if (item.placement.type !== 'categoryItem') return undefined;
        return sortAssets(item.body);
    }, [item]);

    const homeUrl = getPageUrl('home');
    useEffect(() => {
        if (!data) {
            router.replace(homeUrl);
        }
    }, [data, homeUrl, router]);

    if (!data) {
        return null;
    }

    return (
        <ProductDetailContextProviderInner
            data={data}
            item={item}
            parentHash={parentHash}
            id={id}
            itemsMap={itemsMap}
            isChangingByURL={isChangingByURL}
            initialQuantity={initialQuantity}
            initialVariant={initialVariant}
            initialAddons={initialAddons}
            initialMemberVariants={initialMemberVariants}
            initialNote={initialNote}
        >
            {children}
        </ProductDetailContextProviderInner>
    );
};

type InnerProps = {
    children?: ReactNode;
    data: StorefrontItemBody;
    item: ResolvedStorefrontItem;
    parentHash: string;
    id?: string;
    itemsMap: ItemsMap;
    isChangingByURL: boolean;
    initialQuantity: number;
    initialVariant?: string;
    initialAddons?: AddonSelections;
    initialMemberVariants?: Record<string, string>;
    initialNote?: string;
};

const ProductDetailContextProviderInner = ({
    children,
    data,
    item,
    parentHash,
    id,
    itemsMap,
    isChangingByURL,
    initialQuantity,
    initialVariant,
    initialAddons,
    initialMemberVariants,
    initialNote,
}: InnerProps) => {
    const getLocalizedValue = useGetLocalizedValue();
    const searchParams = useSearchParams();
    const variantFromUrl = searchParams?.get('variant');

    const initialVariantHash = initialVariant ?? variantFromUrl ?? parentHash;
    const initialVariantEntry = initialVariantHash !== parentHash ? itemsMap[initialVariantHash] : undefined;
    const initialAddonItem =
        initialVariantEntry && initialVariantEntry.placement.type === 'variant'
            ? applyParentFallbacks(initialVariantEntry.body, item.body)
            : item.body;

    const formMethods = useForm<FormValue>({
        defaultValues: {
            quantity: initialQuantity,
            variant: initialVariantHash,
            addons: initialAddons ?? computeAddonDefaults(initialAddonItem, itemsMap),
            memberVariants: initialMemberVariants ?? {},
            note: initialNote,
        },
    });

    return (
        <FormProvider {...formMethods}>
            <ContextBridge
                data={data}
                slug={getLocalizedValue(item.placement.slug)}
                hash={item.hash}
                id={id}
                itemsMap={itemsMap}
                isChangingByURL={isChangingByURL}
                formMethods={formMethods}
            >
                {children}
            </ContextBridge>
        </FormProvider>
    );
};

type BridgeProps = {
    children?: ReactNode;
    data: StorefrontItemBody;
    slug: string;
    hash: string;
    id?: string;
    itemsMap: ItemsMap;
    isChangingByURL: boolean;
    formMethods: UseFormReturn<FormValue>;
};

const ContextBridge = ({
    children,
    data,
    slug,
    hash,
    id,
    itemsMap,
    isChangingByURL,
    formMethods,
}: BridgeProps) => {
    const selectedVariant = useWatch({ control: formMethods.control, name: 'variant' });

    const currentItem = useMemo(() => {
        if (!selectedVariant || selectedVariant === hash) return data;
        const variantEntry = itemsMap[selectedVariant];
        const variantBody =
            variantEntry && variantEntry.placement.type === 'variant' ? variantEntry.body : undefined;
        if (!variantBody) return data;
        return applyParentFallbacks(sortAssets(variantBody), data);
    }, [selectedVariant, hash, itemsMap, data]);

    const { switchVariant } = useVariantSwitcher({
        parentHash: hash,
        parentItem: data,
        itemsMap,
        isChangingByURL,
    });

    const value = useMemo(
        () => ({ data, currentItem, slug, hash, formMethods, id, switchVariant }),
        [data, currentItem, slug, hash, formMethods, id, switchVariant],
    );

    return <ProductDetailContext.Provider value={value}>{children}</ProductDetailContext.Provider>;
};
